import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Play } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { openInNewTab } from '@/lib/utils';
import { useHasPermission } from '@/lib/permissions';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

import type { Report } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/reports')({
  beforeLoad: () => requirePermission('reports.read'),
  component: ReportsPage,
});

// Aligné sur REPORT_TYPES (reporting-service, services/report.service.ts) — un générateur XLSX par type
const REPORT_TYPES = [
  { type: 'fleet_summary', label: 'Résumé flotte', description: 'Vue d\'ensemble des véhicules : kilométrage, activité, état' },
  { type: 'vehicle_activity', label: 'Activité véhicules', description: 'Temps de conduite, arrêts et distance par véhicule' },
  { type: 'trips', label: 'Trajets', description: 'Détail des trajets sur la période' },
  { type: 'fuel_consumption', label: 'Consommation carburant', description: 'Pleins, litres et coûts par véhicule' },
  { type: 'maintenance_history', label: 'Historique maintenance', description: 'Ordres de travail et coûts' },
  { type: 'alerts_summary', label: 'Synthèse alertes', description: 'Alertes par type et sévérité' },
  { type: 'driver_performance', label: 'Performance conducteurs', description: 'Infractions vitesse, freinages, conduite de nuit' },
] as const;

const TYPE_LABELS: Record<string, string> = Object.fromEntries(REPORT_TYPES.map((r) => [r.type, r.label]));

// Aligné sur l'enum ReportStatus (prisma/schema.prisma)
const STATUS_LABELS: Record<Report['status'], string> = {
  QUEUED: 'En attente',
  RUNNING: 'En cours',
  COMPLETED: 'Prêt',
  FAILED: 'Échec',
};

const STATUS_VARIANT: Record<Report['status'], 'secondary' | 'default' | 'success' | 'destructive'> = {
  QUEUED: 'secondary',
  RUNNING: 'default',
  COMPLETED: 'success',
  FAILED: 'destructive',
};

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

const errorMessage = (err: unknown, fallback: string): string => {
  const e = (err as {
    response?: { data?: { error?: { message?: string; details?: { message: string }[] } } };
  })?.response?.data?.error;
  return e?.details?.length ? e.details.map((d) => d.message).join(' · ') : e?.message ?? fallback;
};

function ReportsPage() {
  const qc = useQueryClient();
  const canGenerate = useHasPermission('reports.generate');
  // Période par défaut : du 1er du mois à aujourd'hui
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return isoDay(new Date(d.getFullYear(), d.getMonth(), 1, 12));
  });
  const [dateTo, setDateTo] = useState(() => isoDay(new Date()));

  const { data, isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: async () => (await api.get<Report[]>('/reports?limit=20')).data,
    // Rafraîchit tant qu'un rapport est en cours de génération
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.status === 'QUEUED' || r.status === 'RUNNING') ? 3_000 : false,
  });

  const launchMutation = useMutation({
    mutationFn: (reportType: string) =>
      api.post<Report>('/reports', { reportType, format: 'XLSX', params: { dateFrom, dateTo } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports'] });
      toast.info('Rapport lancé — génération en cours…');
    },
    onError: (err) => toast.error(errorMessage(err, 'Erreur lors du lancement')),
  });

  const download = async (id: string) => {
    try {
      await openInNewTab(async () => (await api.get<{ url: string }>(`/reports/${id}/download`)).data.url);
    } catch (err) {
      toast.error(errorMessage(err, 'Téléchargement impossible'));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Rapports</h1>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label htmlFor="dateFrom">Du</Label>
          <Input id="dateFrom" type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="dateTo">Au</Label>
          <Input id="dateTo" type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORT_TYPES.map((r) => (
          <Card key={r.type}>
            <CardHeader><CardTitle className="text-base">{r.label}</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{r.description}</p>
              {canGenerate && (
                <Button
                  size="sm"
                  onClick={() => launchMutation.mutate(r.type)}
                  disabled={launchMutation.isPending || !dateFrom || !dateTo}
                >
                  <Play className="h-3 w-3 mr-1" /> Générer (Excel)
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-2">
        <h2 className="font-semibold">Rapports récents</h2>
        {isLoading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
        {data?.map((r) => (
          <div key={r.id} className="bg-card border rounded-lg p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{TYPE_LABELS[r.reportType] ?? r.reportType}</p>
              <p className="text-xs text-muted-foreground">
                {String(r.params['dateFrom'] ?? '')} → {String(r.params['dateTo'] ?? '')} · demandé le{' '}
                {new Date(r.createdAt).toLocaleString('fr-FR')}
              </p>
              {r.status === 'FAILED' && r.errorMsg && <p className="text-xs text-danger mt-1">{r.errorMsg}</p>}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABELS[r.status] ?? r.status}</Badge>
              {r.status === 'COMPLETED' && (
                <Button size="sm" variant="outline" onClick={() => void download(r.id)}>
                  <Download className="h-3 w-3 mr-1" /> Télécharger
                </Button>
              )}
            </div>
          </div>
        ))}
        {data && data.length === 0 && <p className="text-sm text-muted-foreground">Aucun rapport généré</p>}
      </div>
    </div>
  );
}
